import { Injectable, OnDestroy } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { find, get, some, toLower, filter, map, gt, size } from 'lodash';
import { UiActions } from '@store/actions';
import { EntityServiceProvider } from '@shared/services/datasource/entity-service-provider.service';
import { PermissionService } from '@shared/services/session/permission.service';
import { NOT_PERMITTED_ERROR_PAGE_TEMPLATE } from '@text-content/error-page-templates';
import { Observable, Subscription } from '@app/shared/rx';
import { NgRedux, select } from '@angular-redux/store';
import { IAppState } from '@app/store';
import { SupportedAppCodes } from '@shared/types';
import { DASHBOARD_ROUTE } from '@app/routes';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { getSingularEntityName } from '@app/shared/configs';

@Injectable()
export class PermissionGuard
  implements CanActivate, CanActivateChild, OnDestroy
{
  @select(['userSession', 'applicationStarted'])
  public isAppStarted$: Observable<boolean>;
  private subscriptions: Subscription[] = [];

  // eslint-disable-next-line max-params
  public constructor(
    public ngRedux: NgRedux<IAppState>,
    public uiActions: UiActions,
    public permissionService: PermissionService,
    public entityServiceProvider: EntityServiceProvider,
    public i18nService: XVII18nService,
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * Check if user can activate the route
   * Validates application state, subscription, and permissions
   *
   * @param route - The route to activate
   * @returns Promise<boolean> - True if user can activate the route
   */
  public async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    // Step 1: Check application initialization
    if (!this.isApplicationStarted()) {
      return false;
    }

    // Step 2: Validate company subscription
    if (!this.hasValidSubscription()) {
      return this.handleInvalidSubscription();
    }

    // Step 3: Check app-level permissions
    if (!this.permissionService.hasAppLevelPermissions()) {
      return this.handleMissingPermission('inventory-item.View');
    }

    // Step 4: Check DM preferences permission
    if (!this.permissionService.hasDMPreferencesPermission()) {
      return this.handleMissingPermission('preferences.View', false);
    }

    // Step 5: Check route-specific permissions
    const routePermissions = this.getRoutePermissions(route);

    return this.validateRoutePermissions(routePermissions, route);
  }

  public async canActivateChild(
    route: ActivatedRouteSnapshot,
  ): Promise<boolean> {
    return this.canActivate(route);
  }

  public getDefaultPermissions(route: ActivatedRouteSnapshot): string[] {
    const entityType =
      get(route, 'data.entityNameToCheckPermissions') ??
      get(route, 'data.ENTITY_TYPE');
    const defaultPermissions = entityType ? [`${entityType}.View`] : [];
    const entityConfig = get({}, 'config', null);
    const configPath: string = get(route, 'url[0].path') ? 'editor' : 'list';

    return defaultPermissions.concat(
      PermissionService.getRequiredPermissions(entityConfig, configPath),
    );
  }

  /**
   * Check if application is started
   */
  private isApplicationStarted(): boolean {
    const state = this.ngRedux.getState();

    return get(state, ['userSession', 'applicationStarted'], false);
  }

  /**
   * Check if current company has valid XVI subscription
   */
  private hasValidSubscription(): boolean {
    const state = this.ngRedux.getState();
    const companies = get(state, ['userSession', 'companies'], []);
    const currentCompanyId = get(
      state,
      ['userSession', 'currentCompanyId'],
      '',
    );

    const company = find(
      companies,
      (companyItem) => companyItem.id === currentCompanyId,
    );

    return some(
      get(company, 'subscriptions', []),
      (subscription) => subscription.product === toLower(SupportedAppCodes.XVI),
    );
  }

  /**
   * Get route-specific permissions
   */
  private getRoutePermissions(route: ActivatedRouteSnapshot): string[] {
    return get(
      route,
      'data.requiredPermissions',
      this.getDefaultPermissions(route),
    );
  }

  /**
   * Validate route permissions and handle navigation
   */
  private validateRoutePermissions(
    routePermissions: string[],
    route: ActivatedRouteSnapshot,
  ): boolean {
    if (this.permissionService.hasPermissions(...routePermissions)) {
      this.uiActions.hideMenuItems(false);

      return true;
    }
    const permissionDetail = this.permissionService.checkPermissionsDetailed(
      ...routePermissions,
    );
    const missingPermissionNames = map(
      filter(permissionDetail, (permission) => !permission.hasPermission),
      'name',
    );

    if (gt(size(missingPermissionNames), 0)) {
      this.goToErrorPage(
        this.i18nService.translate(I18n.ErrorMessages.needPermissionToView, {
          access: missingPermissionNames.join(', '),
          entity: this.i18nService.translate(
            getSingularEntityName(get(route, 'data.ENTITY_TYPE')),
          ),
        }),
      );

      return false;
    }

    // Redirect to dashboard if no permissions
    this.uiActions.openRoute(DASHBOARD_ROUTE);

    return false;
  }

  /**
   * Handle invalid subscription scenario
   */
  private handleInvalidSubscription(): boolean {
    this.uiActions.hideMenuItems(true);

    return this.goToErrorPage();
  }

  /**
   * Handle missing permissions scenario
   *
   * @param permissionName - The name of the missing permission
   * @param hideMenu - Whether to hide menu items (default: true)
   * @returns Always returns false to prevent route activation
   */
  private handleMissingPermission(
    permissionName: string,
    hideMenu: boolean = true,
  ): boolean {
    if (hideMenu) {
      this.uiActions.hideMenuItems(true);
    }

    return this.goToErrorPage(
      this.i18nService.translate(
        I18n.ErrorMessages.YouAreMissingTheFollowingPermission,
        {
          permission: permissionName,
        },
      ),
    );
  }

  private goToErrorPage(details?: string, description?: string): boolean {
    this.uiActions.showErrorPage({
      ...NOT_PERMITTED_ERROR_PAGE_TEMPLATE,
      ...(details && { details }),
      ...(description && { description }),
    });

    return false;
  }
}
